/* eslint-disable @typescript-eslint/no-explicit-any */
import { IAutocomplete } from "interfaces/IAutocomplete";
import { useRef, useCallback } from "react";
// Reject Candidates
import { useState } from "react";
import { notification } from "antd";
import { useDispatch } from "react-redux";
import { toggleProjectDetailRefresh, toggleStatsRefresh } from "store/slices/talentPool";
import { rejectCandidateFromJob } from "api/talentpool.api";
import moment from "moment";
import { setRejectedCandidate } from "store/slices/refresh.slice";

export const camelize = (string: string): string => {
  return string
    .split(" ")
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1)
    )
    .join("");
};

export const getCurrencyPrice = (price: number, currency: string): string => {
  price = price ? price : 0;
  switch (currency) {
    case "dollar": {
      return `$${price}`;
    }
    case "pound": {
      return `£${price}`;
    }
    case "euro": {
      return `€${price}`;
    }

    default: {
      return `$${price}`;
    }
  }
};

export const parseJwt = (token: string) => {
  if (!token) {
    return;
  }
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace("-", "+").replace("_", "/");
  return JSON.parse(window.atob(base64));
};

type MarkArea = {
  xAxis: number;
};

export const getMarkAreaData = (data: string[] | number[]): MarkArea[][] =>
  data.map((el, index) => [
    {
      xAxis: 2 * index,
    },
    {
      xAxis: 2 * index + 1,
    },
  ]);

export const capitalize = (word: string): string => `${word[0].toUpperCase()}${word.slice(1)}`;

export const hexToRGB = (hex: string, alpha: string | number): string => {
  const r = parseInt(hex.slice(1, 3), 16),
    g = parseInt(hex.slice(3, 5), 16),
    b = parseInt(hex.slice(5, 7), 16);

  return alpha ? `rgba(${r}, ${g}, ${b}, ${alpha})` : `rgb(${r}, ${g}, ${b})`;
};

export const getDifference = (value: number, prevValue: number): string | null =>
  prevValue !== 0 ? `${((Math.abs(value - prevValue) / prevValue) * 100).toFixed(0)}%` : "100%";

export const normalizeProp = (prop: string | number | [number, number]): string =>
  typeof prop === "number"
    ? `${prop}px`
    : (Array.isArray(prop) && `${prop[0]}px ${prop[1]}px`) || prop.toString();

// export const defineColorByPriority = (priority: Priority, theme: DefaultTheme): string => {
//   switch (priority) {
//     case Priority.INFO:
//       return theme.colors.main.primary;
//     case Priority.LOW:
//       return theme.colors.main.success;
//     case Priority.MEDIUM:
//       return theme.colors.main.warning;
//     case Priority.HIGH:
//       return theme.colors.main.error;
//     default:
//       return theme.colors.main.success;
//   }
// };

// export const defineColorBySeverity = (severity: NotificationType | undefined, theme: DefaultTheme): string => {
//   switch (severity) {
//     case 'error':
//       return theme.colors.main.error;
//     case 'warning':
//       return theme.colors.main.warning;
//     case 'success':
//       return theme.colors.main.success;
//     case 'info':
//       return theme.colors.main.primary;
//     default:
//       return theme.colors.main.primary;
//   }
// };

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const mergeBy = (a: any[], b: any[], key: string): any[] =>
  a.filter((elem) => !b.find((subElem) => subElem[key] === elem[key])).concat(b);

export const getSmoothRandom = (factor: number, start: number): number => {
  const halfEnvelope = 1 / factor / 2;
  const max = Math.min(1, start + halfEnvelope);
  const min = Math.max(0, start - halfEnvelope);

  return Math.random() * (max - min) + min;
};

export const trimStr = (str: string, charCount = 15) => {
  if (!str) return "";
  const res = str.substring(0, charCount);
  return str.length > charCount ? res + "..." : res;
};

export const toAbsoluteUrl = (pathname: string) => process.env.PUBLIC_URL + pathname;

export const findIndex = (array: any, key: any, value: any): number => {
  for (let index = 0; index < array.length; index++) {
    if (typeof array[index] === "string") {
      if (array[index] === value) {
        return index;
      }
    } else {
      if (array[index][key] === value) {
        return index;
      }
    }
  }
  return -1; // Element not found
};

export const getInitials = (name: string) => {
  // Split the name into an array of words
  const words = name.split(" ");

  // Initialize an empty string to store initials
  let initials = "";

  // Loop through each word and add the first character (initial) to the initials string
  for (let i = 0; i < words.length; i++) {
    initials += words[i][0];
  }

  // Convert the initials to uppercase and return them
  return initials.toUpperCase();
};

export const getRandomHexColor = () => {
  const letters = "0123456789ABCDEF";
  let color = "#";

  for (let i = 0; i < 6; i++) {
    color += letters[Math.floor(Math.random() * 16)];
  }

  return color;
};

export const formatName = (input: string): string => {
  if (!input) return "";
  // Split the input string by underscores and convert each word to lowercase
  const words = input.toLowerCase().split("_");

  // Capitalize the first letter of each word and join them with a space
  const formattedWords = words.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  // Join the formatted words with a space to create the final formatted string
  const formattedName = formattedWords.join(" ");

  return formattedName;
};

export const getUniqueRecords = (data: any) => {
  const idSet = new Set();
  const uniqueRecords = [];

  for (const item of data) {
    if (!idSet.has(item.id)) {
      idSet.add(item.id);
      uniqueRecords.push(item);
    }
  }

  return uniqueRecords;
};

export const convertToKFormat = (number: number) => {
  if (number === null || number === undefined || isNaN(number)) {
    return "0";
  }

  if (Math.abs(number) >= 1e6) {
    // If the number is in millions, convert it to millions format
    let millions = number / 1e6;
    millions = Math.round(millions * 10) / 10; // Round to one decimal place
    return millions + "M+";
  } else if (Math.abs(number) >= 1000) {
    // If the number is in thousands, convert it to thousands format
    let thousands = number / 1000;
    thousands = Math.round(thousands);
    return thousands + "K+";
  } else {
    // Otherwise, just return the number as is
    return number;
  }
};

export const formatFilterLabel = (field: string): string => {
  // Capitalize the first letter and replace underscores with spaces
  return field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, " ");
};

export const getPages = (array: any[], itemsPerPage: number) => {
  // claculate the total number of pages
  const totalPages = Math.ceil(array?.length / itemsPerPage);

  // create an array to store page
  const pages: { page: number; content: any[] }[] = [];

  // Genrate each page
  for (let page = 1; page <= totalPages; page++) {
    // calculate the start and end index for the crrunt page
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;

    // get the items for the crrunt page
    const pageItems = array?.slice(startIndex, endIndex);

    //add the page content to the array
    pages.push({ page, content: pageItems });
  }

  return pages;
};
export function addNumberField(obj: { number: any }, pageNumber: number, index: number) {
  obj.number = pageNumber * 20 + index + 1;
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);

  // Extract month, day, hour, and minute
  const month: string = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day: string = String(date.getUTCDate()).padStart(2, "0");
  const hours: string = String(date.getUTCHours()).padStart(2, "0");
  const minutes: string = String(date.getUTCMinutes()).padStart(2, "0");

  // Return the formatted string
  return `${month}/${day} ${hours}:${minutes}`;
}

export function createNameLabelArray(data: IAutocomplete): { label: string; value: string }[] {
  // Use optional chaining to handle possible null/undefined values
  const results = data?.data?.autocomplete?.result;

  // Return an empty array if results is null or undefined
  if (!results) {
    return [];
  }

  // Map the results to an array of objects with name and label properties
  return results.map((item) => ({
    value: item,
    label: item,
  }));
}

// Array Method
export function convertToArray(input: string): string[] {
  if (!input || typeof input !== "string") {
    return [];
  }
  return input
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");
}

// Date Method
export const calculateDuration = (
  startDate: string,
  endDate?: string, // Make endDate optional
  includeMonths = true
): string => {
  const start = new Date(startDate);
  const end = endDate ? new Date(endDate) : new Date(); // Use current date if endDate is not provided

  let years = end.getFullYear() - start.getFullYear();
  let months = end.getMonth() - start.getMonth();

  // Adjust if the end month is earlier in the year than the start month
  if (months < 0) {
    years--;
    months += 12;
  }

  if (includeMonths) {
    // return `${years} y, ${months} m`;
    return `${years} year, ${months} months`;
  } else {
    return `${years}.${months}`;
  }
};

// Count Function
export function formatNumber(num: number): string {
  if (num < 1000) return num.toString();
  const units = ["K", "M", "B", "T"];
  const unitIndex = Math.floor(Math.log10(num) / 3);
  const unit = units[unitIndex - 1];
  const shortNum = num / Math.pow(1000, unitIndex);
  return `${shortNum.toFixed(1)}${unit}`;
}

type DebouncedFunction<T extends (...args: any[]) => any> = (...args: Parameters<T>) => void;

export const useDebounce = <T extends (...args: any[]) => any>(
  fn: T,
  delay: number
): DebouncedFunction<T> => {
  const timeoutRef = useRef<number | null>(null);

  const debouncedFn: DebouncedFunction<T> = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        fn(...args);
      }, delay);
    },
    [fn, delay]
  );

  return debouncedFn;
};

export const downloadFile = (data: any, filename: string) => {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link: any = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode.removeChild(link);
};

export const getSubstring = (str: string, numLetters = 2): string => {
  if (!str) return ""; // handle empty or undefined strings
  return str.slice(0, numLetters).toUpperCase(); // Get the first numLetters characters and make them uppercase
};

// Define the types for the parameters
type RejectCandidatesParams = {
  selectedPool: number | null;
  searchId?: number | string;
  handleRejectCandidate?: any;
  selectedCandidates: number | number[]; // Can be a single ID or an array
};
export const useRejectCandidates = () => {
  const dispatch = useDispatch();

  const [loadingReject, setLoadingReject] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const rejectCandidates = async ({ selectedPool, selectedCandidates, searchId, handleRejectCandidate }: RejectCandidatesParams) => {
    // Normalize selectedCandidates to ensure it's an array

    const candidates = Array.isArray(selectedCandidates)
      ? selectedCandidates
      : [selectedCandidates];
    if (selectedPool != null) {
      const payload = {
        jobs: [selectedPool],
        candidates: candidates,
        is_rejected: true,
        search_log_id: searchId ?? ""
      };
      setLoadingReject(true);
      setError(null); // Reset error state
      setSuccess(false); // Reset success state
      try {
        const response = await rejectCandidateFromJob(payload);
        setSuccess(true);
        notification.success({
          message: "Success",
          description: "Candidate rejected successfully.",
        });
        handleRejectCandidate?.(selectedCandidates);
        dispatch(
          setRejectedCandidate(
            Array.isArray(selectedCandidates) ? selectedCandidates : [selectedCandidates]
          )
        );
        dispatch(toggleProjectDetailRefresh());
        dispatch(toggleStatsRefresh());
      } catch (err) {
        notification.error({
          message: "Error",
          description: "Failed to reject candidate(s).",
        });
        setError(err as Error); // Capture the error as Error type
      } finally {
        setLoadingReject(false);
      }
    } else {
      setError(new Error("Selected pool is null.")); // Create an Error object for clarity
    }
  };
  return { rejectCandidates, loadingReject, error, success };
};

export const getDate = (date: string): string => {
  if (!date) return "";
  return moment(date).format("MM/DD/YYYY");
};

export const getDate2 = (date?: string): string => {
  const parsedDate = date ? moment(date) : moment();
  return parsedDate.format("DD MMMM YYYY"); // 27 May 2025
};
